'use client';
import React from 'react';
import { ChevronRight, ChevronLeft, CheckSquare, Square } from 'lucide-react';
import { TutorConfig } from './AITutorBuilderContent';

interface Props {
  config: TutorConfig;
  updateConfig: (u: Partial<TutorConfig>) => void;
  onNext: () => void;
  onBack: () => void;
}

const availableCourses = [
  { id: 'Intro to Coding', wing: 'Intermediate', color: 'text-blue-600' },
  { id: 'Logic & Problem Solving', wing: 'Intermediate', color: 'text-blue-600' },
  { id: 'Earth & Space Science', wing: 'Intermediate', color: 'text-blue-600' },
  { id: 'Digital Citizenship', wing: 'Intermediate', color: 'text-blue-600' },
  { id: 'Basic Mathematics', wing: 'Foundations', color: 'text-emerald-600' },
  { id: 'Science Basics', wing: 'Foundations', color: 'text-emerald-600' },
  { id: 'Advanced Mathematics', wing: 'Advanced', color: 'text-purple-600' },
  { id: 'AI Ethics & Safety', wing: 'Advanced', color: 'text-purple-600' },
];

const difficultyOptions = [
  { id: 'beginner', label: 'Beginner', desc: 'Simple explanations, lots of encouragement' },
  { id: 'intermediate', label: 'Intermediate', desc: 'Balanced challenge with guided discovery' },
  { id: 'advanced', label: 'Advanced', desc: 'Deep dives, technical precision, case studies' },
];

const goalOptions = [
  'Build coding skills', 'Prepare for exams', 'Explore curiosity', 'Earn a certificate',
  'Strengthen weak subjects', 'Get ahead in school', 'Career preparation', 'Personal enrichment',
];

export default function TutorBuilderStep2({ config, updateConfig, onNext, onBack }: Props) {
  const toggleCourse = (id: string) => {
    const updated = config.courses.includes(id)
      ? config.courses.filter((c) => c !== id)
      : [...config.courses, id];
    updateConfig({ courses: updated });
  };

  const toggleGoal = (g: string) => {
    const updated = config.goals.includes(g)
      ? config.goals.filter((x) => x !== g)
      : [...config.goals, g];
    updateConfig({ goals: updated });
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-foreground mb-1">Curriculum Selection</h2>
        <p className="text-sm text-muted-foreground">Choose which courses your tutor focuses on and set the learning difficulty.</p>
      </div>

      {/* Course selection */}
      <div>
        <label className="block text-sm font-semibold text-foreground mb-1.5">Focus Courses</label>
        <p className="text-xs text-muted-foreground mb-3">Select the courses your tutor should specialize in. You can add more later.</p>
        <div className="space-y-2">
          {availableCourses.map((course) => {
            const selected = config.courses.includes(course.id);
            return (
              <button
                key={`course-sel-${course.id}`}
                type="button"
                onClick={() => toggleCourse(course.id)}
                className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-all text-left btn-scale ${
                  selected ? 'border-primary bg-primary/5' : 'border-border bg-card hover:bg-muted/50'
                }`}
              >
                {selected ? (
                  <CheckSquare size={18} className="text-primary shrink-0" />
                ) : (
                  <Square size={18} className="text-muted-foreground shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-foreground">{course.id}</p>
                  <p className={`text-xs font-medium ${course.color}`}>{course.wing} Wing</p>
                </div>
              </button>
            );
          })}
        </div>
        {config.courses.length === 0 && (
          <p className="text-xs text-amber-600 mt-2">⚠ Select at least one course to continue</p>
        )}
      </div>

      {/* Difficulty */}
      <div>
        <label className="block text-sm font-semibold text-foreground mb-1.5">Learning Difficulty</label>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {difficultyOptions.map((d) => (
            <button
              key={`diff-sel-${d.id}`}
              type="button"
              onClick={() => updateConfig({ difficulty: d.id })}
              className={`p-4 rounded-xl border-2 text-left transition-all btn-scale ${
                config.difficulty === d.id ? 'border-primary bg-primary/5' : 'border-border bg-card hover:border-muted-foreground/30'
              }`}
            >
              <p className="text-sm font-bold text-foreground mb-1">{d.label}</p>
              <p className="text-xs text-muted-foreground">{d.desc}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Goals */}
      <div>
        <label className="block text-sm font-semibold text-foreground mb-1.5">Learning Goals</label>
        <p className="text-xs text-muted-foreground mb-3">What is this student trying to achieve? Select all that apply.</p>
        <div className="flex flex-wrap gap-2">
          {goalOptions.map((g) => (
            <button
              key={`goal-${g}`}
              type="button"
              onClick={() => toggleGoal(g)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-all btn-scale ${
                config.goals.includes(g)
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-card border-border text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
            >
              {g}
            </button>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-2 px-5 py-2.5 border border-border rounded-xl text-sm font-semibold text-muted-foreground hover:bg-muted transition-colors"
        >
          <ChevronLeft size={16} /> Back
        </button>
        <button
          type="button"
          onClick={onNext}
          disabled={config.courses.length === 0}
          className="flex items-center gap-2 px-6 py-2.5 bg-primary text-primary-foreground font-semibold rounded-xl btn-scale hover:bg-primary/90 transition-colors disabled:opacity-50"
        >
          Continue to Behavior <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
}